import { Component } from '@theme/component';

/**
 * Barre d'annonce — rotation des messages ou défilement continu.
 *
 * En défilement, le contenu est dupliqué jusqu'à couvrir deux fois la largeur :
 * sans ça la boucle laisse un vide, et on ne peut pas le savoir depuis Liquid.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [track]
 * @property {HTMLElement[]} [messages]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #index = 0;
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.mode === 'scrolling') {
      this.#fill();
      new ResizeObserver(() => this.#fill()).observe(this);
      // Même précaution : la largeur peut être inconnue au premier passage.
      requestAnimationFrame(() => this.#fill());
      setTimeout(() => this.#fill(), 300);
      return;
    }

    const delay = Number(this.dataset.autoplay) || 0;
    if (delay && (this.refs.messages?.length ?? 0) > 1) {
      this.#timer = window.setInterval(() => this.next(), delay * 1000);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timer) window.clearInterval(this.#timer);
  }

  next() {
    this.#move(1);
  }

  prev() {
    this.#move(-1);
  }

  #move(direction) {
    const messages = this.refs.messages;
    if (!messages?.length) return;

    messages[this.#index]?.classList.remove('anb__message--active');
    this.#index = (this.#index + direction + messages.length) % messages.length;
    messages[this.#index]?.classList.add('anb__message--active');
  }

  #fill() {
    const track = this.refs.track;
    if (!track) return;

    const width = Math.round(this.offsetWidth);
    if (!width) return;
    // Sans ce garde-fou, modifier les enfants redéclenche l observateur en boucle.
    if (Number(track.dataset.width) === width) return;
    track.dataset.width = String(width);

    if (!track.dataset.base) {
      track.dataset.base = String(track.children.length);
    }
    const baseCount = Number(track.dataset.base);
    const base = [...track.children].slice(0, baseCount).map((node) => node.cloneNode(true));

    track.replaceChildren(...base.map((node) => node.cloneNode(true)));

    const addSet = () => {
      for (const node of base) {
        const copy = node.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        track.appendChild(copy);
      }
    };

    // Toujours au moins deux jeux : avec un seul, le décalage vaut la piste
    // entière et l'écran reste vide en fin de boucle.
    addSet();

    // Période exacte en pixels (un jeu + son espace) : un pourcentage ignorerait
    // l'espace de fin manquant et ferait sauter la boucle de quelques pixels.
    const first = /** @type {HTMLElement} */ (track.children[0]);
    const second = /** @type {HTMLElement} */ (track.children[baseCount]);
    const period = second.offsetLeft - first.offsetLeft;
    if (period <= 0) return;

    let sets = 2;
    while (track.scrollWidth - period < width && sets < 20) {
      addSet();
      sets++;
    }

    track.style.setProperty('--anb-shift', `-${period}px`);
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
